<script>
  import {
    selectedPerformanceMeasures,
    budgetBlocks,
  } from "@stores/userData.js";
  import ShareableButtons from "./ShareableButtons.svelte";

  import Basic from "./Cards/Basic.svelte";
  import TopFour from "./Cards/TopFour.svelte";
  import SpecificDepartment from "./Cards/SpecificDepartment.svelte";
  import SpecificProgram from "./Cards/SpecificProgram.svelte";

  const cards = [Basic, TopFour, SpecificDepartment, SpecificProgram];

  let width;

  $: cardMargin = width * 0.025;

  const cardPercentOfScreenWidth = 0.9;
  $: cardWidth = width * cardPercentOfScreenWidth;
  $: cardHeight = cardWidth * 0.55;

  $: offset = currentCard * cardWidth * -1;

  let currentCard = 0;
  const move = (direction) => {
    if (direction > 0 && currentCard != cards.length - 1) {
      currentCard++;
    } else if (direction < 0 && currentCard != 0) {
      currentCard--;
    }
  };

  import { toJpeg } from "html-to-image";
  let downloadable;
  let downloading = false;
  const download = function () {
    if (downloading) return;
    downloading = true;
    toJpeg(downloadable).then(function (dataUrl) {
      downloading = false;

      var link = document.createElement("a");
      link.download = "Houston Budget Shareable.jpeg";
      link.href = dataUrl;
      link.click();
    });
  };

  // SWIPING
  let xDown = null;
  let yDown = null;

  const X_SWIPE_THRESHOLD = 10; // How intense swipe must be. Might remove this

  function handleTouchStart(evt) {
    if (!evt?.touches) return;
    xDown = evt.touches[0].clientX;
    yDown = evt.touches[0].clientY;
  }

  function handleTouchMove(evt) {
    if (!evt?.touches) return;

    if (!xDown || !yDown) {
      return;
    }

    var xUp = evt.touches[0].clientX;
    var yUp = evt.touches[0].clientY;

    var xDiff = xDown - xUp;
    var yDiff = yDown - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (Math.abs(xDiff) < X_SWIPE_THRESHOLD) return;
      if (xDiff > 0) {
        move(1);
      } else {
        move(-1);
      }
    }
    /* reset values */
    xDown = null;
  }
</script>

<div
  class="carousel-wrapper"
  class:downloading
  on:touchstart={(e) => {
    handleTouchStart(e);
  }}
  on:touchmove={(e) => {
    handleTouchMove(e);
  }}
>
  <div tabindex="-1" aria-hidden="true" class="hidden-current-card-container">
    <div
      class="hidden-current-card"
      style="min-width: {cardWidth - cardMargin}px;
          height: {cardHeight}px; 
          position: absolute;"
      bind:this={downloadable}
    >
      <svelte:component this={cards[currentCard]} />
    </div>
  </div>
  <div
    class="carousel"
    style="padding-left: {((1 - cardPercentOfScreenWidth) * 100) / 2}vw;"
    bind:clientWidth={width}
  >
    <button
      class="arrow"
      style="left: 5vw; padding-right: 1px;"
      class:disabled={currentCard == 0}
      on:click={() => move(-1)}>&lsaquo;</button
    >
    <button
      class="arrow"
      style="right: calc(5vw + {cardMargin}px; padding-left: 1px;"
      class:disabled={currentCard == cards.length - 1}
      on:click={() => move(1)}>&rsaquo;</button
    >
    {#if $selectedPerformanceMeasures.length > 0 && $budgetBlocks.length > 0}
      <div class="items" style="transform: translateX({offset}px);">
        {#each cards as card, i}
          <div
            id="card-{i}"
            class:active={i == currentCard}
            class="item"
            style="min-width: {cardWidth - cardMargin}px;
                height: {cardHeight}px;
                margin-right: {cardMargin}px;"
          >
            <!-- Click listener lives outside of the actual card, but will be sized identically, so that we can place in-card buttons (change department) above via z-index and not trigger download when we click them -->
            <button
              class="click-listener"
              tabindex={i == currentCard ? 0 : -1}
              on:click={() => {
                i == currentCard ? download(i) : (currentCard = i);
              }}
              style="min-width: {cardWidth - cardMargin}px;
                 height: {cardHeight}px;"
            >
              <ShareableButtons {downloading} />
            </button>
            <svelte:component this={card} active={i == currentCard} />
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  /* Wrapper is fullbleed */
  .carousel-wrapper {
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    margin-top: 2.5rem;
  }

  .carousel {
    width: 100%;
  }

  .items {
    display: flex;
    transition: transform 0.4s ease-in-out;
    transform: translateX(0px);
  }

  .item {
    cursor: pointer;
    height: 400px;
    border-radius: 0.7rem;
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
    box-shadow: 1px 1px 15px rgba(0, 0, 0, 0.7);
    transition: opacity 500ms ease, box-shadow 200ms ease;
    position: relative;
    pointer-events: none;
  }

  .item:not(.active) {
    box-shadow: none;
    opacity: 0.5;
  }

  @media screen and (min-width: 1068px) {
    .carousel {
      transform: scale(0.85);
    }
  }

  .hidden-current-card-container {
    transform: translateX(100vw);
    width: 0;
    height: 0;
  }

  .click-listener {
    position: absolute;
    pointer-events: all;
    z-index: 1;
    cursor: pointer;
  }

  .downloading,
  .downloading * {
    cursor: progress;
  }

  .arrow {
    position: absolute;
    background: var(--accent-color);
    color: var(--primary-color);
    display: flex;
    justify-content: center;
    cursor: pointer;
    z-index: 3;
    font-weight: 100;
    transition: opacity 300ms ease;
    top: -2.75rem;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    font-size: 2rem;
    line-height: 1.925rem;
  }

  .disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (max-width: 668px) {
    .arrow {
      top: -2rem;
      width: 1.5rem;
      height: 1.5rem;
      font-size: 1.5rem;
      line-height: 1.25rem;
    }
  }
</style>
